/**
 * Termos de Uso — placeholder MVP. Substituir pelo texto definitivo do
 * jurídico antes de assinar primeiros contratos comerciais.
 *
 * Página estática, pública, indexável.
 */
import Head from "next/head";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { Brand } from "@/components/Brand";
import { SmartBackLink } from "@/components/SmartBackLink";

export default function TermosPage() {
  const { t } = useTranslation();
  const lastUpdate = "08 de maio de 2026";

  return (
    <>
      <Head>
        <title>{t("legal.terms.metaTitle", "Termos de Uso — Miller Platform")}</title>
        <meta name="description" content={t("legal.terms.metaDesc", "Termos de uso da Miller Platform.")} />
      </Head>

      <div className="min-h-screen bg-app-bg">
        {/* Header */}
        <header className="border-b border-app bg-app-surface">
          <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
            <Link href="/" className="hover:opacity-80 transition-opacity">
              <Brand />
            </Link>
            <SmartBackLink className="text-sm text-app-2 hover:text-app-1 font-medium">
              {t("legal.back", "Voltar")}
            </SmartBackLink>
          </div>
        </header>

        {/* Conteúdo */}
        <main className="max-w-3xl mx-auto px-6 py-12">
          <p className="text-xs font-semibold text-app-3 uppercase tracking-widest mb-2">
            {t("legal.terms.eyebrow", "DOCUMENTO LEGAL")}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-app-1 tracking-tight mb-3">
            {t("legal.terms.title", "Termos de Uso")}
          </h1>
          <p className="text-sm text-app-3 mb-10">
            {t("legal.terms.lastUpdate", "Última atualização")}: {lastUpdate}
          </p>

          <div className="prose-sm space-y-6 text-app-2 leading-relaxed">
            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                1. {t("legal.terms.s1.title", "Aceitação dos termos")}
              </h2>
              <p>
                {t("legal.terms.s1.body", "Ao acessar e usar a Miller Platform (\"Plataforma\"), você concorda integralmente com estes Termos de Uso. Se não concordar com qualquer parte, não deve utilizar a Plataforma.")}
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                2. {t("legal.terms.s2.title", "Descrição do serviço")}
              </h2>
              <p>
                {t("legal.terms.s2.body", "A Miller Platform é uma solução SaaS (software como serviço) multi-tenant que oferece módulos de gestão empresarial, conciliação contábil, automação de tarefas e assistente de IA, entre outros.")}
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                3. {t("legal.terms.s3.title", "Conta de usuário")}
              </h2>
              <p>
                {t("legal.terms.s3.body", "Você é responsável por manter a confidencialidade das credenciais da sua conta e por todas as atividades realizadas com elas. Notifique-nos imediatamente em caso de uso não autorizado.")}
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                4. {t("legal.terms.s4.title", "Uso aceitável")}
              </h2>
              <p>
                {t("legal.terms.s4.body", "Você concorda em não utilizar a Plataforma para qualquer atividade ilegal, fraudulenta, que viole direitos de terceiros ou que possa danificar a infraestrutura ou outros usuários.")}
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                5. {t("legal.terms.s5.title", "Disponibilidade")}
              </h2>
              <p>
                {t("legal.terms.s5.body", "Buscamos manter a Plataforma sempre disponível, mas não garantimos operação ininterrupta. Manutenções programadas serão comunicadas com antecedência sempre que possível.")}
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                6. {t("legal.terms.s6.title", "Propriedade intelectual")}
              </h2>
              <p>
                {t("legal.terms.s6.body", "Todo o conteúdo, design e código da Miller Platform são de propriedade exclusiva da Miller ou de seus licenciantes. Você mantém a propriedade dos dados que insere na Plataforma.")}
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                7. {t("legal.terms.s7.title", "Limitação de responsabilidade")}
              </h2>
              <p>
                {t("legal.terms.s7.body", "Na máxima extensão permitida por lei, a Miller não será responsável por danos indiretos, incidentais ou consequenciais decorrentes do uso da Plataforma.")}
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                8. {t("legal.terms.s8.title", "Alterações nos termos")}
              </h2>
              <p>
                {t("legal.terms.s8.body", "Podemos atualizar estes Termos periodicamente. Alterações materiais serão comunicadas com pelo menos 30 dias de antecedência. O uso continuado após a alteração configura aceitação.")}
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                9. {t("legal.terms.s9.title", "Contato")}
              </h2>
              <p>
                {t("legal.terms.s9.body", "Dúvidas sobre estes Termos podem ser enviadas para")}{" "}
                <a href="mailto:legal@millersolutions.com.br" className="text-app-1 underline hover:opacity-80">
                  legal@millersolutions.com.br
                </a>
                .
              </p>
            </section>

            <section className="pt-6 border-t border-app-subtle">
              <p className="text-xs text-app-3">
                {t("legal.terms.placeholder", "Este é um documento preliminar. A versão definitiva será publicada após revisão jurídica.")}
              </p>
            </section>
          </div>

          {/* Footer com link cruzado */}
          <div className="mt-12 pt-6 border-t border-app-subtle flex items-center gap-4 text-xs text-app-3">
            <Link href="/privacidade" className="hover:text-app-2 underline underline-offset-2">
              {t("legal.privacyLink", "Política de Privacidade")}
            </Link>
            <span>·</span>
            <Link href="/" className="hover:text-app-2 underline underline-offset-2">
              {t("legal.homeLink", "Início")}
            </Link>
          </div>
        </main>
      </div>
    </>
  );
}
